import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button, Select, Tag, Tabs, Spin, message, Tooltip} from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  MailOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import ReactQuill from "react-quill";
import {DEFAULT_BODY} from "../../../pages/dashboard/automations/email/_components/constants";
import {
  getEditFormValuesFromApi,
  resolveOfferingIdsForMultiselect,
} from "../../../pages/dashboard/automations/email/_components/editFormData";
import {normalizeEmailAutomation} from "../../../pages/dashboard/automations/email/_components/types";
import type {EmailAutomationApiItem} from "../../../pages/dashboard/automations/email/_components/types";
import {
  useCreateEmailAutomation,
  useGetAllEmailAutomationsForOfferingStep,
  useGetOfferingActiveEmailAutomations,
  useLinkAutomationToOffering,
  useUnlinkAutomationFromOffering,
  useUpdateEmailAutomation,
  automationConflictsOfferingTriggerDelay,
  OFFERING_EMAIL_AUTOMATION_DUPLICATE_SCHEDULE_MESSAGE,
} from "../../../react-query-hooks/email-automation";
import {transformFormToApiPayload} from "../../../pages/dashboard/automations/email/_components/api-types";
import type {EmailAutomationOfferingOption} from "../../../pages/dashboard/automations/email/_components/api-types";
import {
  createAutomationSchema,
  draftAutomationSchema,
  type CreateAutomationFormValues,
} from "../../../pages/dashboard/automations/email/_components/schema";
import {ValidationError} from "yup";
import type {EmailAutomationRow} from "../../../pages/dashboard/automations/email/_components/types";
import {insertEmailAutomationShortCode} from "../../../pages/dashboard/automations/email/_components/insert-short-code";
import {formatAutomationTimingLabel} from "../../../pages/dashboard/automations/email/_components/automation-timing-label";
import {
  EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE,
  EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE,
  formTriggerToApiTrigger,
  isApiTriggerAtOrOverLimit,
  pickDefaultTriggerForCreate,
  triggerQuotaBlockedFromCatalog,
} from "../../../pages/dashboard/automations/email/_components/email-automation-limits";
import {
  CreateAutomationDrawer,
  type DrawerView,
  type OfferingsDrawerAutomationKind,
} from "../../../pages/dashboard/automations/email/_components/CreateAutomationDrawer";
import {
  StyledAutomationRowActions,
  StyledAutomationRowBody,
  StyledAutomationRowCard,
  StyledAutomationRowIcon,
  StyledAutomationRowTags,
  StyledAutomationRowTitle,
  StyledAutomationsEmpty,
  StyledAutomationsHeaderRow,
  StyledAutomationsListStack,
  StyledAutomationsPanel,
  StyledAutomationsSubtitle,
  StyledAutomationsTabBarRow,
  StyledAutomationsTitle,
  StyledAutomationsTabsWrap,
  StyledAutomationsTypeFilter,
  StyledOfferingAutomationsWrapper,
} from "./styles";
import {useGetOfferings} from "@/react-query-hooks/offerings";
import {useEmailAutomationSaveErrorUI} from "../../../pages/dashboard/automations/email/_components/useEmailAutomationSaveErrorUI";

type AutomationsTab = "active" | "all";
type TypeFilter = "all" | "email" | "file";

const FILE_DELIVERY_AUTOMATION_TYPE_DISABLED_TOOLTIP =
  "File delivery automations are not available yet.";

const OFFERINGS_CREATE_GATE_FALLBACK_MESSAGE =
  "Complete all required fields before creating this automation.";

const defaultFormValues: CreateAutomationFormValues = {
  automationName: "",
  subject: "Thank you for your purchase, {Customer_name}",
  body: DEFAULT_BODY,
  addCustomButton: false,
  buttonText: "",
  buttonLink: "",
  trigger: "after_purchase",
  offerings: [],
  sendTiming: "immediately",
  delayValue: 5,
  delayUnit: "minutes",
};

function newAutomationDefaults(
  contextOfferingId: number | null | undefined
): CreateAutomationFormValues {
  const id = contextOfferingId;
  return {
    ...defaultFormValues,
    offerings: id != null && id > 0 ? [String(id)] : [],
  };
}

function resolveContextOfferingName(
  offeringId: number | null,
  explicit: string | undefined,
  options: EmailAutomationOfferingOption[]
): string {
  if (explicit) return explicit;
  if (offeringId == null || offeringId <= 0) return "Offering";
  const o = options.find((x) => x.offeringId === offeringId);
  return o?.offeringName ?? "Offering";
}

function triggerDisplayLabel(item: EmailAutomationApiItem): string {
  return item.trigger === "after-purchase" ? "After purchase" : "Abandoned purchase";
}

function getRowTimingContext(item: EmailAutomationApiItem): "purchase" | "abandonment" {
  return item.trigger === "after-purchase" ? "purchase" : "abandonment";
}

interface OfferingAutomationsProps {
  contextOfferingId?: number | null;
  /** Shown in optimistic link pivot when the offering is not in the general/offerings list. */
  contextOfferingName?: string;
  onPublishSuccess?: () => void;
  renderWizardFooter?: () => React.ReactNode;
}

export function OfferingAutomations({
  contextOfferingId: contextOfferingIdProp,
  contextOfferingName,
  onPublishSuccess: _onPublishSuccess,
  renderWizardFooter,
}: OfferingAutomationsProps) {
  const contextOfferingId = contextOfferingIdProp ?? null;
  const canLink = contextOfferingId != null && contextOfferingId > 0;

  const offeringStepManageBlockedTooltip = useMemo(() => {
    if (canLink) return undefined;
    return "Save this offering before you can add or remove automations.";
  }, [canLink]);

  const [mainTab, setMainTab] = useState<AutomationsTab>("active");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<EmailAutomationRow | null>(null);
  const [drawerView, setDrawerView] = useState<DrawerView>("form");
  const [shortCodeTarget, setShortCodeTarget] = useState<"subject" | "body" | null>(null);

  const [drawerAutomationKind, setDrawerAutomationKind] =
    useState<OfferingsDrawerAutomationKind>("email");

  const subjectInputRef = useRef<HTMLInputElement | null>(
    null
  ) as React.MutableRefObject<HTMLInputElement | null>;
  const quillRef = useRef<ReactQuill>(null);
  const subjectCursorPos = useRef<number | null>(null);
  const bodyCursorPos = useRef<number | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: {errors},
  } = useForm<CreateAutomationFormValues>({
    resolver: yupResolver(createAutomationSchema),
    mode: "onSubmit",
    defaultValues: newAutomationDefaults(contextOfferingId),
  });

  const watchedFormForPublishGate = watch();
  const offeringsCreatePublishValidation = useMemo(() => {
    try {
      createAutomationSchema.validateSync(watchedFormForPublishGate, {
        abortEarly: false,
      });
      return {passes: true as const, gateMessages: [] as string[]};
    } catch (err: unknown) {
      if (err instanceof ValidationError) {
        const fromInner = Array.from(
          new Set(
            err.inner.map((i) => i.message).filter((m): m is string => Boolean(m?.trim()))
          )
        );
        if (fromInner.length > 0) {
          return {passes: false as const, gateMessages: fromInner};
        }
        const single =
          typeof err.message === "string" && err.message.trim()
            ? err.message.trim()
            : null;
        return {
          passes: false as const,
          gateMessages: single ? [single] : [OFFERINGS_CREATE_GATE_FALLBACK_MESSAGE],
        };
      }
      return {
        passes: false as const,
        gateMessages: [OFFERINGS_CREATE_GATE_FALLBACK_MESSAGE],
      };
    }
  }, [watchedFormForPublishGate]);

  const offeringsCreatePassesPublishSchema = offeringsCreatePublishValidation.passes;

  const offeringsQuery = useGetOfferings();
  const allEmailAutomationsQuery = useGetAllEmailAutomationsForOfferingStep();
  const offeringActiveAutomationsQuery =
    useGetOfferingActiveEmailAutomations(contextOfferingId);
  const createEmailAutomation = useCreateEmailAutomation();
  const updateEmailAutomation = useUpdateEmailAutomation();
  const linkToOffering = useLinkAutomationToOffering();
  const unlinkFromOffering = useUnlinkAutomationFromOffering();
  const isDrawerSubmitting =
    createEmailAutomation.isLoading || updateEmailAutomation.isLoading;

  const offeringOptions = useMemo(
    () =>
      (offeringsQuery.data ?? []).map((o) => ({
        value: o.value,
        label: o.label,
        offeringId: o.offeringId,
        offeringName: o.offeringName,
      })),
    [offeringsQuery.data]
  );

  const availableOfferings = offeringsQuery.data ?? [];
  const resolvedOfferingName = resolveContextOfferingName(
    contextOfferingId,
    contextOfferingName,
    availableOfferings
  );

  const emailAutomationsCatalog = useMemo(
    () => allEmailAutomationsQuery.data ?? [],
    [allEmailAutomationsQuery.data]
  );

  const emailAutomationsForAllTab = useMemo(
    () => emailAutomationsCatalog.filter((item) => item.draft !== true),
    [emailAutomationsCatalog]
  );
  const activeItems = useMemo(
    () => offeringActiveAutomationsQuery.data ?? [],
    [offeringActiveAutomationsQuery.data]
  );
  const linkedAutomationIds = useMemo(
    () => new Set(activeItems.map((a) => a.id)),
    [activeItems]
  );

  const triggerQuotaBlockedOffering = useMemo(
    () => triggerQuotaBlockedFromCatalog(emailAutomationsCatalog),
    [emailAutomationsCatalog]
  );

  const offeringBothTriggersAtAutomationLimit =
    triggerQuotaBlockedOffering.after_purchase &&
    triggerQuotaBlockedOffering.abandoned_purchase;

  const createNewHeaderTooltip = useMemo(() => {
    if (offeringBothTriggersAtAutomationLimit) {
      return EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE;
    }
    return undefined;
  }, [offeringBothTriggersAtAutomationLimit]);

  const emptyStateCreateNewBlocked = offeringBothTriggersAtAutomationLimit;

  const emptyStateCreateNewBlockedTooltip =
    EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE;

  const allTabDisplayedItems = useMemo((): EmailAutomationApiItem[] => {
    switch (typeFilter) {
      case "all":
      case "email":
        return emailAutomationsForAllTab;
      case "file":
        return [];
      default:
        return emailAutomationsForAllTab;
    }
  }, [emailAutomationsForAllTab, typeFilter]);

  const listForCurrentTab: EmailAutomationApiItem[] =
    mainTab === "active" ? activeItems : allTabDisplayedItems;

  const activeCount = activeItems.length;
  const allTabCount = emailAutomationsForAllTab.length;

  const listAreaLoading =
    mainTab === "all"
      ? allEmailAutomationsQuery.isLoading
      : canLink && offeringActiveAutomationsQuery.isLoading;

  useEffect(() => {
    if (editingRecord || !isDrawerOpen) return;
    if (contextOfferingId == null || contextOfferingId <= 0) return;
    const opts = offeringsQuery.data ?? [];
    const idToken =
      opts.length > 0
        ? resolveOfferingIdsForMultiselect([contextOfferingId], opts)[0]
        : String(contextOfferingId);
    const current = (getValues("offerings") as string[] | undefined) ?? [];
    if (current.includes(idToken)) return;
    setValue("offerings", current.length === 0 ? [idToken] : [...current, idToken]);
  }, [
    contextOfferingId,
    isDrawerOpen,
    editingRecord,
    getValues,
    setValue,
    offeringsQuery.data,
  ]);

  useEffect(() => {
    if (!isDrawerOpen || !editingRecord) return;
    const opts = offeringsQuery.data;
    if (!opts?.length) return;
    const raw = emailAutomationsCatalog.find(
      (item) => String(item.id) === editingRecord.key
    );
    const pivot = raw?.settings?.pivot ?? [];
    if (pivot.length === 0) return;
    const pivotIds = pivot.map((p) => p.offeringId);
    setValue("offerings", resolveOfferingIdsForMultiselect(pivotIds, opts), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [
    isDrawerOpen,
    editingRecord,
    emailAutomationsCatalog,
    offeringsQuery.data,
    setValue,
  ]);

  const openDrawerCreate = () => {
    const blocked = triggerQuotaBlockedFromCatalog(emailAutomationsCatalog);
    if (blocked.after_purchase && blocked.abandoned_purchase) {
      message.warning(EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE);
      return;
    }
    setEditingRecord(null);
    setDrawerAutomationKind("email");
    reset({
      ...newAutomationDefaults(contextOfferingId),
      trigger: pickDefaultTriggerForCreate(blocked),
    });
    setDrawerView("form");
    setShortCodeTarget(null);
    setIsDrawerOpen(true);
  };

  const openDrawerEdit = (raw: EmailAutomationApiItem) => {
    const row = normalizeEmailAutomation(raw);
    setEditingRecord(row);
    setDrawerAutomationKind("email");
    const values = getEditFormValuesFromApi(raw, availableOfferings);
    reset({
      ...values,
      trigger: raw.trigger === "after-purchase" ? "after_purchase" : "abandoned_purchase",
    });
    setDrawerView("form");
    setShortCodeTarget(null);
    setIsDrawerOpen(true);
  };

  const resetDrawerState = () => {
    setIsDrawerOpen(false);
    setEditingRecord(null);
    setDrawerAutomationKind("email");
    setDrawerView("form");
    setShortCodeTarget(null);
    reset(newAutomationDefaults(contextOfferingId));
  };

  const {handleSaveError, ScheduleConflictModal} =
    useEmailAutomationSaveErrorUI(resetDrawerState);

  const insertShortCode = (code: string) =>
    insertEmailAutomationShortCode(code, {
      target: shortCodeTarget,
      getSubject: () => getValues("subject") || "",
      setValue,
      subjectInputRef,
      subjectCursorPos,
      bodyCursorPos,
      quillRef,
      onAfterInsert: () => {
        setDrawerView("form");
        setShortCodeTarget(null);
      },
    });

  const onCreateOrUpdate = (values: CreateAutomationFormValues) => {
    const payload = transformFormToApiPayload(values, false, availableOfferings);

    if (editingRecord) {
      updateEmailAutomation.mutate(
        {id: editingRecord.key, payload},
        {onSuccess: () => resetDrawerState(), onError: handleSaveError}
      );
    } else {
      if (
        isApiTriggerAtOrOverLimit(
          emailAutomationsCatalog,
          formTriggerToApiTrigger(values.trigger)
        )
      ) {
        message.error(EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE);
        return;
      }
      createEmailAutomation.mutate(payload, {
        onSuccess: () => resetDrawerState(),
        onError: handleSaveError,
      });
    }
  };

  const onSaveDraft = (values: CreateAutomationFormValues) => {
    const payload = transformFormToApiPayload(values, true, availableOfferings);
    if (editingRecord) {
      updateEmailAutomation.mutate(
        {id: editingRecord.key, payload},
        {onSuccess: () => resetDrawerState(), onError: handleSaveError}
      );
    } else {
      if (
        isApiTriggerAtOrOverLimit(
          emailAutomationsCatalog,
          formTriggerToApiTrigger(values.trigger)
        )
      ) {
        message.error(EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE);
        return;
      }
      createEmailAutomation.mutate(payload, {
        onSuccess: () => resetDrawerState(),
        onError: handleSaveError,
      });
    }
  };

  const onSaveDraftClick = async () => {
    const values = getValues();
    try {
      const validated = await draftAutomationSchema.validate(values, {abortEarly: false});
      clearErrors();
      onSaveDraft(validated as CreateAutomationFormValues);
    } catch (err: unknown) {
      if (err instanceof Error && "inner" in err) {
        const yupErr = err as {inner: {path: string; message: string}[]};
        clearErrors();
        yupErr.inner.forEach(({path, message: msg}) => {
          setError(path as keyof CreateAutomationFormValues, {message: msg});
        });
      }
    }
  };

  const onLink = (item: EmailAutomationApiItem) => {
    if (!canLink) return;
    if (automationConflictsOfferingTriggerDelay(item, activeItems)) {
      message.error(OFFERING_EMAIL_AUTOMATION_DUPLICATE_SCHEDULE_MESSAGE);
      return;
    }
    linkToOffering.mutate({
      automationId: item.id,
      offeringId: contextOfferingId!,
      offeringName: resolvedOfferingName,
      automation: item,
    });
  };

  const onUnlink = (item: EmailAutomationApiItem) => {
    if (!canLink) return;
    unlinkFromOffering.mutate({
      automationId: item.id,
      offeringId: contextOfferingId!,
    });
  };

  const renderRow = (item: EmailAutomationApiItem, mode: "active" | "all") => {
    const formValues = getEditFormValuesFromApi(item, availableOfferings);
    const timingLabel = formatAutomationTimingLabel(
      formValues,
      getRowTimingContext(item)
    );
    const linked = linkedAutomationIds.has(item.id);

    return (
      <StyledAutomationRowCard key={item.id}>
        <StyledAutomationRowIcon>
          <MailOutlined style={{fontSize: 18}} />
        </StyledAutomationRowIcon>
        <StyledAutomationRowBody>
          <StyledAutomationRowTitle>{item.automationName}</StyledAutomationRowTitle>
          <StyledAutomationRowTags>
            <Tag
              style={{
                borderRadius: 6,
                backgroundColor: "#F3F4F6",
                color: "#4B5563",
                border: "none",
                padding: "0.4rem 1.2rem",
              }}
            >
              {triggerDisplayLabel(item)}
            </Tag>
            <Tag
              icon={<ClockCircleOutlined />}
              style={{
                borderRadius: 6,
                backgroundColor: "#F3F4F6",
                color: "#4B5563",
                border: "none",
                padding: "0.4rem 1.2rem",
              }}
            >
              {timingLabel}
            </Tag>
          </StyledAutomationRowTags>
        </StyledAutomationRowBody>
        <StyledAutomationRowActions>
          {mode === "all" && (
            <>
              {linked ? (
                <Button icon={<CheckOutlined />} type="text" size="small" disabled>
                  Added
                </Button>
              ) : (
                <Tooltip title={canLink ? undefined : offeringStepManageBlockedTooltip}>
                  <span style={{display: "inline-block"}}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      disabled={!canLink}
                      loading={linkToOffering.isLoading}
                      onClick={() => onLink(item)}
                      style={{
                        borderRadius: 8,
                        padding: "0.1rem 1rem",
                        height: 32,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      Add
                    </Button>
                  </span>
                </Tooltip>
              )}
            </>
          )}
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            aria-label="Edit automation"
            onClick={() => openDrawerEdit(item)}
          />
          {mode === "active" && (
            <Tooltip title={canLink ? undefined : offeringStepManageBlockedTooltip}>
              <span style={{display: "inline-block"}}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Remove from offering"
                  disabled={!canLink}
                  loading={unlinkFromOffering.isLoading}
                  onClick={() => onUnlink(item)}
                />
              </span>
            </Tooltip>
          )}
        </StyledAutomationRowActions>
      </StyledAutomationRowCard>
    );
  };

  const showEmailEmptyInAll =
    mainTab === "all" &&
    !allEmailAutomationsQuery.isLoading &&
    allTabDisplayedItems.length === 0;

  return (
    <StyledOfferingAutomationsWrapper>
      <StyledAutomationsPanel>
        <StyledAutomationsHeaderRow>
          <div>
            <StyledAutomationsTitle>Automations</StyledAutomationsTitle>
            <StyledAutomationsSubtitle>
              Run actions automatically when customers interact with this offering.
            </StyledAutomationsSubtitle>
          </div>
        </StyledAutomationsHeaderRow>

        <StyledAutomationsTabBarRow>
          <StyledAutomationsTabsWrap>
            <Tabs
              activeKey={mainTab}
              onChange={(k) => setMainTab(k as AutomationsTab)}
              items={[
                {key: "active", label: `Active (${activeCount})`},
                {key: "all", label: `All (${allTabCount})`},
              ]}
            />
          </StyledAutomationsTabsWrap>
          <Tooltip title={createNewHeaderTooltip}>
            <span style={{display: "inline-block"}}>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={openDrawerCreate}
                disabled={offeringBothTriggersAtAutomationLimit}
                style={{
                  background: "#1c1726",
                  borderColor: "#1c1726",
                  borderRadius: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                Create new
              </Button>
            </span>
          </Tooltip>
        </StyledAutomationsTabBarRow>

        {mainTab === "all" && (
          <StyledAutomationsTypeFilter>
            {/* <span style={{fontSize: 14, color: "#595959"}}>Type</span> */}
            <Select
              style={{minWidth: 200}}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v as TypeFilter)}
              options={[
                {value: "all", label: "All types"},
                {value: "email", label: "Email"},
                {
                  value: "file",
                  label: (
                    <span title={FILE_DELIVERY_AUTOMATION_TYPE_DISABLED_TOOLTIP}>
                      File delivery <Tag color="#8c8c8c">SOON</Tag>
                    </span>
                  ),
                  disabled: true,
                },
              ]}
            />
          </StyledAutomationsTypeFilter>
        )}

        {listAreaLoading ? (
          <div style={{textAlign: "center", padding: 48}}>
            <Spin />
          </div>
        ) : mainTab === "active" && activeItems.length === 0 ? (
          <StyledAutomationsEmpty>
            <div>No automations yet</div>
            <div className="empty-links">
              <span
                className="empty-link"
                onClick={() => {
                  setMainTab("all");
                  setTypeFilter("all");
                }}
              >
                Select from existing
              </span>{" "}
              or{" "}
              {emptyStateCreateNewBlocked ? (
                <Tooltip title={emptyStateCreateNewBlockedTooltip}>
                  <span
                    style={{
                      color: "#8c8c8c",
                      cursor: "not-allowed",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    create a new one
                  </span>
                </Tooltip>
              ) : (
                <span className="empty-link" onClick={openDrawerCreate}>
                  create a new one
                </span>
              )}
              .
            </div>
          </StyledAutomationsEmpty>
        ) : showEmailEmptyInAll ? (
          <StyledAutomationsEmpty>
            You do not have any email automations yet. Create one from Automations in the
            dashboard or use Create new.
          </StyledAutomationsEmpty>
        ) : (
          <StyledAutomationsListStack>
            {listForCurrentTab.map((item) => renderRow(item, mainTab))}
          </StyledAutomationsListStack>
        )}
      </StyledAutomationsPanel>

      {renderWizardFooter ? renderWizardFooter() : null}

      <CreateAutomationDrawer
        visible={isDrawerOpen}
        isEditMode={editingRecord !== null}
        onClose={resetDrawerState}
        currentView={drawerView}
        onViewChange={setDrawerView}
        offeringsFlow={{
          automationKind: drawerAutomationKind,
          onAutomationKindChange: setDrawerAutomationKind,
          createFormMeetsPublishSchema: offeringsCreatePassesPublishSchema,
          createFormGateMessages: offeringsCreatePublishValidation.gateMessages,
          editLinkedOfferingCount: editingRecord?.offerings,
        }}
        formProps={{
          control,
          errors,
          watch,
          setValue,
          clearErrors,
          offeringOptions: offeringOptions,
          isOfferingsLoading: offeringsQuery.isLoading,
          subjectInputRef,
          quillRef,
          subjectCursorPos,
          bodyCursorPos,
          onOpenShortCodes: (target) => {
            setShortCodeTarget(target);
            setDrawerView("shortcodes");
          },
          onOpenPreview: () => setDrawerView("preview"),
          triggerQuotaBlocked: editingRecord ? undefined : triggerQuotaBlockedOffering,
        }}
        handleSubmit={handleSubmit}
        watch={watch}
        onSubmit={onCreateOrUpdate}
        onSaveDraft={onSaveDraft}
        onSaveDraftClick={onSaveDraftClick}
        onInsertShortCode={insertShortCode}
        isSubmitting={isDrawerSubmitting}
        onDeleteAndClose={resetDrawerState}
      />

      {ScheduleConflictModal}
    </StyledOfferingAutomationsWrapper>
  );
}
